import React, { useEffect, useRef, useState } from 'react';
import '@github/relative-time-element';
import { __ } from '@wordpress/i18n';

// components
import ActivityDraft from '../ActivityDraft';
import CommentReplies from '../CommentReplies';
import RichText from '../RichText';
import ModalGenerateActivity from '../../../ModalGenerateActivity';

// icons
import CircleCheckIcon from '../../../../icons/CircleCheckIcon';
import TaskAssignedIcon from '../../../../icons/TaskAssignedIcon';

// store
import { useCommCenter } from '../../context';

/** What became of the comment, once an activity was confirmed from it. */
const linkedActivityLabel = (linked) => {
    switch (linked.status) {
        case 'completed':
            return __('Attività completata', 'soundbetween');
        case 'in_review':
            return __('Attività in revisione', 'soundbetween');
        case 'dismissed':
            return __('Attività annullata', 'soundbetween');
        default:
            return linked.assigneeName
                ? __('Attività assegnata a %s', 'soundbetween').replace('%s', linked.assigneeName)
                : __('Attività assegnata', 'soundbetween');
    }
}

/**
 * One communication. Comments collected from external sources (WhatsApp,
 * Telegram, mail) say so in the header. When a comment contains an actionable
 * request, "Genera attività" drafts the matching activity inline.
 *
 * A comment is also told apart from the activity it produced: the card always
 * states whether an activity is attached to it, and links to it when there is
 * one, so the two feeds no longer have to be matched up by eye.
 */
const CommentCard = ({ comment }) => {
    const {
        toggleCommentResolved,
        generateDraft,
        openLinkedActivity,
        highlightCommentId,
        can,
        pending,
        generatingFor,
    } = useCommCenter();
    const [expanded, setExpanded] = useState(false);
    const [naming, setNaming] = useState(false);

    // Jumped to from the Activities tab: bring the card into view and mark it.
    const isHighlighted = highlightCommentId === comment.id;
    const cardRef = useRef(null);

    useEffect(() => {
        if (isHighlighted) cardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [isHighlighted]);

    const replyCount = comment.repliesCount || 0;
    // Producers and directors close anybody's thread, everybody else their own.
    const canResolve = can.resolveThread(comment);
    const linked = comment.linkedActivity;
    const canGenerate = can.generateActivity && !comment.draft && !linked;
    const isGenerating = pending?.generate && generatingFor === comment.id;

    return (
        <article
            ref={cardRef}
            className={`commentCard ${comment.resolved ? 'commentCard--resolved' : ''} ${comment.source ? 'commentCard--external' : ''} ${isHighlighted ? 'is-highlighted' : ''}`}>
            <header className="commentCard__header">
                <p className="commentCard__author">
                    <span className="commentCard__authorName">{comment.author.name}</span>
                    {comment.author.role && (
                        <span className="commentCard__authorRole">{` | ${comment.author.role}`}</span>
                    )}
                    {comment.source && (
                        <span className="commentCard__source">{` · ${__('da', 'soundbetween')} ${comment.source}`}</span>
                    )}
                </p>
                {(canResolve || comment.resolved) && (
                    <button
                        type="button"
                        className={`commentCard__resolve ${comment.resolved ? 'is-active' : ''}`}
                        disabled={comment.resolved || !canResolve || pending?.resolve}
                        onClick={() => toggleCommentResolved(comment.id)}
                        title={comment.resolved
                            ? __('Commento risolto', 'soundbetween')
                            : __('Segna il commento come risolto', 'soundbetween')}>
                        <CircleCheckIcon size={16}/>
                    </button>
                )}
            </header>

            <p className="commentCard__body">
                <RichText segments={comment.body}/>
            </p>

            {linked && (
                <button
                    type="button"
                    className="commentCard__activityLink"
                    onClick={() => openLinkedActivity(linked.id)}
                    title={__('Apri l’attività nata da questo commento', 'soundbetween')}>
                    <TaskAssignedIcon size={14}/>
                    {linkedActivityLabel(linked)}
                </button>
            )}

            {canGenerate && (
                <button
                    type="button"
                    className="commButton commButton--accent"
                    disabled={isGenerating}
                    onClick={() => setNaming(true)}>
                    {isGenerating
                        ? __('Genero attività…', 'soundbetween')
                        : __('Genera attività', 'soundbetween')}
                </button>
            )}

            {!linked && !comment.draft && !can.generateActivity && (
                <p className="commentCard__noActivity">
                    {__('Nessuna attività collegata', 'soundbetween')}
                </p>
            )}

            {comment.draft && <ActivityDraft comment={comment}/>}

            {canGenerate && (
                <ModalGenerateActivity
                    visible={naming}
                    onHide={() => setNaming(false)}
                    onSubmit={(name) => generateDraft(comment.id, name)}/>
            )}

            <footer className="commentCard__footer">
                <relative-time class="commentCard__time" datetime={comment.createdAt}/>
                {replyCount > 0 && (
                    <>
                        <span className="commentCard__dot">·</span>
                        <button
                            type="button"
                            className="commentCard__repliesToggle"
                            onClick={() => setExpanded(!expanded)}>
                            {expanded
                                ? __('Nascondi risposte', 'soundbetween')
                                : `${replyCount} ${replyCount === 1 ? __('risposta', 'soundbetween') : __('risposte', 'soundbetween')}`}
                        </button>
                    </>
                )}
                {replyCount === 0 && can.comment && (
                    <>
                        <span className="commentCard__dot">·</span>
                        <button
                            type="button"
                            className="commentCard__repliesToggle"
                            onClick={() => setExpanded(!expanded)}>
                            {expanded ? __('Chiudi', 'soundbetween') : __('Rispondi', 'soundbetween')}
                        </button>
                    </>
                )}
            </footer>

            {expanded && <CommentReplies comment={comment}/>}
        </article>
    )
}

export default CommentCard;
